import type { ComponentPropsWithoutRef } from "react";
import { Slot } from "radix-ui";

import { MUI_TYPOGRAPHY_VARIANTS } from "@/components/ui/mui-typography-shape";
import { cn } from "@/lib/utils";

// MUI's `Link` under the donor theme — `@modsy/react-ui`'s `Link`, which the
// specifications screens compose in 21 files and the catalog in 5
// (`docs/migration/research/timmy-spec-ui-inventory.md` §3).
//
// `Link.mjs` is `styled(Typography)` at `variant='inherit'`, so the anchor
// takes the surrounding type whole; `color='primary'` puts it in
// `primary.main`; and `underline` decides the decoration:
//
//   - `always` (MUI's default): underlined, in the link's colour at 0.4
//     (`getTextDecoration.mjs`), going to the full colour on hover,
//   - `hover`: bare at rest, underlined under the pointer — the catalog's item
//     names (`item-card.tsx:224`, `item-data-row.tsx:204`),
//   - `none`: never.
//
// No hover colour change and no transition: MUI's link has neither.
//
// The donor passes `component={RouterLink}` for in-app links; here the caller
// hands its router `Link` over through `asChild`, so this file needs no router
// and an external `href` needs no special case.

const UNDERLINE_CLASSES = {
  always: "underline decoration-lennar-primary-underline hover:decoration-current",
  hover: "no-underline hover:underline",
  none: "no-underline",
} as const;

/**
 * `inherit` sets no underline colour — MUI only sets `--Link-underlineColor`
 * for a palette colour — so an `always` underline follows the ink.
 */
const COLOR_CLASSES = {
  primary: "text-lennar-blue-600",
  inherit: "text-inherit decoration-current",
  /**
   * `text.secondary` — the Editor's per-plan links (`LinePerPlan.tsx:46,55`),
   * whose `sx` `textDecoration: 'underline'` is a shorthand that resets the
   * underline's colour to the ink, so an `always` underline follows it (#809).
   */
  textSecondary: "text-lennar-ink-secondary decoration-current",
} as const;

export interface MuiLinkProps extends ComponentPropsWithoutRef<"a"> {
  /** MUI's `underline`; `always` when omitted, as MUI defaults it. */
  underline?: keyof typeof UNDERLINE_CLASSES;
  /** MUI's `color`; `primary` when omitted. */
  color?: keyof typeof COLOR_CLASSES;
  /** MUI's `variant`; `inherit` when omitted — the surrounding type whole. */
  variant?: keyof typeof MUI_TYPOGRAPHY_VARIANTS;
  /** Dress the one child element (a router `Link`) instead of rendering an `<a>`. */
  asChild?: boolean;
}

export function MuiLink({
  underline = "always",
  color = "primary",
  variant,
  asChild = false,
  className,
  ...props
}: MuiLinkProps) {
  const Comp = asChild ? Slot.Root : "a";

  return (
    <Comp
      {...props}
      className={cn(
        variant && MUI_TYPOGRAPHY_VARIANTS[variant],
        UNDERLINE_CLASSES[underline],
        COLOR_CLASSES[color],
        className,
      )}
    />
  );
}
